import { OrderItem, TicketItem } from "../entity";
import { SearchResult, Event, Ticket } from "webshop-api";
import { GTMUtils } from "../GTM.utils";
import { ItemType } from "../item-type.enum";
import { DateTimeUtil } from '../../date-time';
import { AnalyticsStorage } from "../storage";
import { AnalyticsSessionStore } from "../../storage";
import { CartTicket } from "./ticket-factory.interfaces";

export class GTMTicketItemFactory {

    private static getStructure(overrides?: Partial<TicketItem>): TicketItem {
        return {
            item_id: "-",
            item_name: "-",
            index: 0,
            price: 0,
            quantity: 1,
            coupon: "-",
            discount: 0,
            item_category: "-",
            item_category2: "-",
            item_list_id: "-",
            item_list_name: "-",
            item_partner_id: "-",
            item_partner_name: "-",
            item_event_organizer_id: "-",
            item_event_organizer_name: "-",
            item_event_date: "-",
            item_days_to_event: 0,
            item_event_city: "-",
            item_event_venue: "-",
            item_artist_id: "-",
            item_artist_name: "-",
            item_seat_selection: "-",
            item_eventseries_id: "-",
            item_eventseries_name: "-",
            item_variant: "-",
            ...overrides,
        }
    }

    public static fromOrderItem(data: OrderItem, index: number): TicketItem {
        const date = new Date(data.date * 1000).toDateString();

        return this.getStructure({
            index,
            item_id: data.id,
            item_name: data.name,
            item_partner_id: data.partnerId.toString(10),
            item_partner_name: data.partnerName,
            item_event_organizer_name: data.organizer,
            item_event_date: GTMUtils.formatToAnalyticsDateFrom(date),
            item_days_to_event: DateTimeUtil.getDaysDifference(date),
            item_event_city: data.city,
            item_event_venue: data.venue,
            item_variant: data.variant,
            price: data.price,
            quantity: data.quantity,
        });
    }

    public static fromTicketSelection(
        ticket: CartTicket,
        ticketSelectionMode: string,
        eventName: string,
        eventId: string,
    ): TicketItem {
        const item = this.getStructure({
            item_name: eventName,
            item_id: eventId,
            item_category: 'ticket',
            item_seat_selection: `${ticketSelectionMode} selection`,
            price: ticket.price,
            quantity: ticket.amount,
            item_variant: ticket.name ? `${ticket.priceCategoryName} - ${ticket.name}` : '-',
        });

        const analyticsStore = AnalyticsStorage.getInstance();
        const sessionData = analyticsStore && analyticsStore.getSessionStore() ?
            <AnalyticsSessionStore>analyticsStore.getSessionStore()
            : null;

        if (analyticsStore && sessionData && sessionData.activeEvent) {
            const event = analyticsStore.getSessionStore()!.activeEvent;

            if (event) {
                item.item_event_date = event.item_event_date;
                item.item_days_to_event = event.item_days_to_event;
                item.item_event_city = event.item_event_city;
                item.item_event_venue = event.item_event_venue;
                item.item_eventseries_id = event.item_eventseries_id;
                item.item_eventseries_name = event.item_eventseries_name;
            }
        }

        if (sessionData && sessionData.activeEventSeries) {
            const eventSeries = sessionData.activeEventSeries;
            item.item_eventseries_id = eventSeries.item_id;
            item.item_eventseries_name = eventSeries.item_name;
        }

        this.attachCommonData(item, null, false);

        return item;
    }

    public static fromEticket(data: Ticket, event: Event, index: number, pool: number): TicketItem {
        const date = event.startDateTime.dateTime.toString();

        return this.getStructure({
            index,
            item_id: event.eventId.toString(10),
            item_name: event.name,
            item_partner_id: pool.toString(10),
            item_event_date: GTMUtils.formatToAnalyticsDateFrom(date),
            item_days_to_event: DateTimeUtil.getDaysDifference(date),
            item_event_city: event.venue.city,
            item_event_venue: event.venue.name,
            price: 0,
            quantity: 1,
        });
    }

    public static fromSearchResult(data: SearchResult, index: number, listName?: string): TicketItem {
        const partialPromotionItem = {
            index: index ?? 0,
            item_name: data.title ?? '-',
            item_category: data.itemType ?? '',
        }

        const promotionItem = this.getStructure(partialPromotionItem);

        if (data.events) {
            GTMUtils.attachEventSpecificDataTo(promotionItem, data.events);
        }

        if (listName) {
            promotionItem.item_list_name = listName;
            promotionItem.item_list_id = listName;
        }

        if (data.itemType === 'artist' && data.title) {
            promotionItem.item_artist_name = data.title;
        }

        this.attachCommonData(promotionItem, data.url ?? null);

        return promotionItem;
    }

    public static fromEventList(element: HTMLElement, attachEventSeriesData = false): TicketItem | null {
        const ga4Data: HTMLElement | null = element.querySelector('[data-js=ga4-data]');

        if (!ga4Data) {
            return null;
        }

        const promotionItem = this.getStructure({...JSON.parse(ga4Data.innerHTML)});
        const url = GTMUtils.getEventListItemUrl(element);

        if (attachEventSeriesData) {
            const analyticsStore = AnalyticsStorage.getInstance();
            const sessionData = analyticsStore && analyticsStore.getSessionStore() ?
                <AnalyticsSessionStore>analyticsStore.getSessionStore()
                : null;

            if (sessionData && sessionData.activeEventSeries) {
                const eventSeries = sessionData.activeEventSeries;
                promotionItem.item_eventseries_id = eventSeries.item_id;
                promotionItem.item_eventseries_name = eventSeries.item_name;
            }
        }

        this.attachCommonData(promotionItem, url);
        this.attachItemDataFromUrl(promotionItem, url);

        return promotionItem;
    }

    private static attachCommonData(item: TicketItem, url: string | null, attachEventSpecificData = true) {
        if (
            attachEventSpecificData
            && item.item_event_date
            && item.item_event_date !== '-'
        ) {
            item.item_days_to_event = GTMUtils.calculateDaysUntilEventFrom(item.item_event_date);
            item.item_event_date = GTMUtils.formatToAnalyticsDateFrom(item.item_event_date);
        }

        const isOnHomepage = !!document.querySelector('[data-js=homepage]');

        if (url) {
            this.attachItemIdAndCategory(item, url);
        }

        if (isOnHomepage && url) {
            this.attachItemName(item, url);
        }

        this.attachItemDataFromUrl(item, url);
        this.attachSessionData(item);
    }

    private static attachSessionData(item: TicketItem) {
        const analyticsStorage = AnalyticsStorage.getInstance();
        const sessionData = analyticsStorage && analyticsStorage.getSessionStore() ?
            <AnalyticsSessionStore>analyticsStorage.getSessionStore()
            : null;

        if (!sessionData) {
            return;
        }

        item.item_partner_id = sessionData.poolId!.toString() ?? '-';

        if (
            (item.item_list_name === '-' || GTMUtils.isOnArtistEventseriesOrCityPage())
            && sessionData.activeList
        ) {
            item.item_list_name = sessionData.activeList;
            item.item_list_id = sessionData.activeList;
        }
    }

    private static attachItemName(item: TicketItem, url: string) {
        const itemType: ItemType = GTMUtils.getItemTypeFrom(url);

        item.item_name = `${itemType}-${item.item_id}`;
    }

    private static attachItemIdAndCategory(item: TicketItem, url: string): void {
        const id = GTMUtils.parseItemIdFrom(url);
        const itemType: ItemType = GTMUtils.getItemTypeFrom(url);
        item.item_category = itemType.toLowerCase();

        if (id) {
            item.item_id = id;
        }
    }

    private static attachItemDataFromUrl(item: TicketItem, url: string | null): void {
        if (!url) {
            return;
        }

        if (url.includes('profile.html')) {
            item.item_artist_id = item.item_id;
        }

        if (url.includes('event.html')) {
            item.item_eventseries_id = item.item_id;
        }
    }

}
